import { useEffect } from "react";
import { FieldLabel } from "../ui/field";
import { useTranslation } from "react-i18next";

type Props = {
  visible: boolean;
  disabled?: boolean;
  publicTokenizationKey: string;
  onReadyChange?: (ready: boolean) => void;

  lastPaymentRequestIdRef: React.MutableRefObject<string | null>;
  pendingResolveRef: React.MutableRefObject<
    null | ((v: { token: string; card?: any }) => void)
  >;
  pendingRejectRef: React.MutableRefObject<null | ((e: any) => void)>;
  setNaxellaSubmitting?: (v: boolean) => void;
};

const isCollectMounted = () =>
  !!(window as any).CollectJS?.iframes?.cvv?.isConnected;

export function NaxellaFields({
  visible,
  disabled,
  publicTokenizationKey,
  onReadyChange,
  lastPaymentRequestIdRef,
  pendingResolveRef,
  pendingRejectRef,
  setNaxellaSubmitting,
}: Props) {
  const { t } = useTranslation();

  const hardResetCollectInline = () => {
    try {
      document.querySelectorAll("iframe.CollectJSInlineIframe").forEach((el) => el.remove());

      // Clear the mount points so CollectJS can re-inject cleanly
      ["ccnumber", "ccexp", "cvv"].forEach((id) => {
        const node = document.getElementById(id);
        if (node) node.innerHTML = "";
      });

      if ((window as any).CollectJS?.iframes) {
        (window as any).CollectJS.iframes = {};
      }
    } catch {}
  };

  useEffect(() => {
    if (!visible || disabled) return;

    if (isCollectMounted()) {
      onReadyChange?.(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        onReadyChange?.(false);

        // load script
        const src = "https://naxella.transactiongateway.com/token/Collect.js";
        // loadScript won't set attributes; we need to ensure the script tag has the tokenization key.
        // We'll implement a small specialized loader here.
        await new Promise<void>((resolve, reject) => {
          if ((window as any).CollectJS) return resolve();

          const existing = document.querySelector(
            `script[src="${src}"]`
          ) as HTMLScriptElement | null;
          
          if (existing) {
            // script tag exists but CollectJS not ready yet — wait for it
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", reject, { once: true });
            return;
          }

          const script = document.createElement("script");
          script.src = src;
          script.async = true;
          script.setAttribute("data-tokenization-key", publicTokenizationKey);

          // Apple Pay requires price/country/currency on the script tag itself.
          // Even if we don't want to show Apple Pay, omitting these causes:
          // "Could not create PaymentRequestAbstraction" if the gateway key
          // has Apple Pay enabled. We satisfy the requirement but hide the button.
          script.setAttribute("data-price", "0.00");
          script.setAttribute("data-country", "CA");
          script.setAttribute("data-currency", "CAD");
          script.setAttribute("data-field-apple-pay-selector", "#applepay-sink");
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);
        });

        if (cancelled) return;
        if (!(window as any).CollectJS) throw new Error("CollectJS failed to load");
        hardResetCollectInline();

        (window as any).CollectJS.configure({
          paymentType: "cc",
          variant: "inline",
          googleFont: "",
          validationCallback: function (_field: any, status: boolean) {
            if (status === false) {
              lastPaymentRequestIdRef.current = null;

              pendingRejectRef.current?.(new Error("Card validation failed"));
              pendingResolveRef.current = null;
              pendingRejectRef.current = null;

              setNaxellaSubmitting?.(false);
            }
          },
          fieldsAvailableCallback: function () {
            const frames = document.querySelectorAll("iframe.CollectJSInlineIframe");
            frames.forEach((frame) => {
              const f = frame as HTMLElement;

              // prevent duplicate listeners
              if (f.dataset.fsBound === "1") return;
              f.dataset.fsBound = "1";

              const reset = () => {
                lastPaymentRequestIdRef.current = null;
                setNaxellaSubmitting?.(false);
              };

              frame.addEventListener("blur", reset, { passive: true });
              frame.addEventListener("focus", reset, { passive: true });
            });
          },
          callback: function (response: any) {
            try {
              if (response?.token) {
                const expectedRequestId = lastPaymentRequestIdRef.current;
                const receivedRequestId = response?.initiatedBy?.detail?.requestId;
                
                const ok = !!expectedRequestId && !!receivedRequestId && receivedRequestId === expectedRequestId;

                if (ok) {
                  lastPaymentRequestIdRef.current = null;

                  pendingResolveRef.current?.({
                    token: response.token,
                    card: response.card,
                  });
                  pendingResolveRef.current = null;
                  pendingRejectRef.current = null;
                  return;
                } else {
                  setNaxellaSubmitting?.(false);
                }
                return;
              }

              if (response?.error) {
                lastPaymentRequestIdRef.current = null;
                pendingRejectRef.current?.(response.error);
                pendingResolveRef.current = null;
                pendingRejectRef.current = null;
                setNaxellaSubmitting?.(false);
              }
            } catch (e) {
              lastPaymentRequestIdRef.current = null;
              pendingRejectRef.current?.(e);
              pendingResolveRef.current = null;
              pendingRejectRef.current = null;
              setNaxellaSubmitting?.(false);
            }
          },
          invalidCss: { color: "#dc3545" },
          fields: {
            ccnumber: { placeholder: "•••• •••• •••• ••••" },
            ccexp: { placeholder: "MM / YY" },
            cvv: { placeholder: "CVV" },
          },
        });

        onReadyChange?.(true);
      } catch (e) {
        onReadyChange?.(false);
      }
    })();

    return () => {
      cancelled = true;
      onReadyChange?.(false);
      hardResetCollectInline();

      lastPaymentRequestIdRef.current = null;
      pendingResolveRef.current = null;
      pendingRejectRef.current = null;
      setNaxellaSubmitting?.(false);
    };
  }, [visible, disabled, publicTokenizationKey]);

  if (!visible || disabled) return;

  return (
    <>
      <div className="mb-5 gap-1">
        <FieldLabel className="flex gap-1 mb-1">
          {t("models.paymentInfo.cardNumber")}
          <span className="text-red-500">*</span>
        </FieldLabel>
        <div
          id="ccnumber"
          className="h-9 rounded-md border border-input px-3 py-2 flex items-center"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div>
          <FieldLabel className="flex gap-1 mb-1">
            {t("models.paymentInfo.dateExpiration")}
            <span className="text-red-500">*</span>
          </FieldLabel>
          <div
            id="ccexp"
            className="h-9 rounded-md border border-input px-3 py-2 flex items-center"
          />
        </div>
        <div>
          <FieldLabel className="flex gap-1 mb-1">
            {t("models.paymentInfo.cvc")}
            <span className="text-red-500">*</span>
          </FieldLabel>
          <div
            id="cvv"
            className="h-9 rounded-md border border-input px-3 py-2 flex items-center"
          />
        </div>
      </div>
    </>
  );
}
